import type {OmitStrict} from 'type-zoo'
import type {TMajorActionForcedDealData, TPlayerId} from '@kapitalist/contract/types'
import {skins} from '~/game/skins'
import {ForcedDealCard} from '~/models/game/Cards/ActionCard'
import {propertyCardFactory} from '~/models/game/factories'
import {PlayerGamePosition} from '~/models/game/GamePosition'
import type {TMajorActionUserFacingDescriptionData} from '~/models/game/MajorAction'
import MajorAction from '~/models/game/MajorAction'
import {unambiguousNeighborhoodDescriptionData} from '~/models/game/MajorActions/utils'
import type {AnyPropertyCard} from '~/models/game/types'
import {sentenceCase, xVerbY} from '~/utils/language'

type PublicMembersFromApi = OmitStrict<
  TMajorActionForcedDealData,
  'card' | 'type' | 'offeredCard' | 'requestedCard'
>

export default class MajorActionForcedDeal
  extends MajorAction<TMajorActionForcedDealData>
  implements PublicMembersFromApi
{
  card: ForcedDealCard
  minorActorPlayerId: TPlayerId
  offeredCard: AnyPropertyCard
  requestedCard: AnyPropertyCard

  constructor(data: TMajorActionForcedDealData) {
    super(data)
    this.card = new ForcedDealCard(data.card)
    this.minorActorPlayerId = data.minorActorPlayerId
    this.offeredCard = propertyCardFactory(data.offeredCard)
    this.requestedCard = propertyCardFactory(data.requestedCard)
  }

  public userFacingDescription({
    tense,
    skin,
    gamePosition,
  }: TMajorActionUserFacingDescriptionData): string {
    const currentPlayer =
      gamePosition instanceof PlayerGamePosition
        ? gamePosition.currentPlayer
        : undefined
    const majorActor = gamePosition.majorActorPlayer
    const minorActor = gamePosition.players.find(
      o => o.id === this.minorActorPlayerId
    )
    if (!minorActor) {
      throw new Error(`Minor actor not found in MajorActionForcedDeal description`)
    }

    const portfolioNeighborhoodOfOfferedCard =
      majorActor.portfolio.neighborhoods.find(pn =>
        pn.properties.find(p => p.id === this.offeredCard.id)
      )
    if (!portfolioNeighborhoodOfOfferedCard) {
      // TODO - the major actor isn't always the player whose perspective we currently have
      //  e.g. if I look at my options (`availableMovesFromHand...`) during someone else's turn
      //  maybe this idea of a perspective would be useful as a first-class idea in e.g. GameData
      //  for this issue, and for spectators & replays. could also use tense here to understand
      //  when 'future' that we're looking at "our" future options w/ this card?
      throw new Error(
        `Forced Deal's offered card not found in major actor's portfolio`
      )
    }
    const {
      sameColorCount: offeredSameColorCount,
      sameColorIndex: offeredSameColorIndex,
    } = unambiguousNeighborhoodDescriptionData(
      minorActor.portfolio,
      portfolioNeighborhoodOfOfferedCard
    )

    const portfolioNeighborhoodOfRequestedCard =
      minorActor.portfolio.neighborhoods.find(pn =>
        pn.properties.find(p => p.id === this.requestedCard.id)
      )
    if (!portfolioNeighborhoodOfRequestedCard) {
      throw new Error(
        `Forced Deal's requested card not found in minor actor's portfolio`
      )
    }
    const {
      sameColorCount: requestedSameColorCount,
      sameColorIndex: requestedSameColorIndex,
    } = unambiguousNeighborhoodDescriptionData(
      minorActor.portfolio,
      portfolioNeighborhoodOfRequestedCard
    )

    const dropFirstNoun = tense === 'imperativePrompt'
    const {conjugatedVerb, xNoun, yNounPossessive, xNounPossessive} = xVerbY(
      tense,
      majorActor,
      dropFirstNoun ? 'Play' : 'play',
      minorActor,
      currentPlayer?.id
    )

    const actor = dropFirstNoun
      ? undefined
      : xNoun === majorActor.username
        ? xNoun
        : sentenceCase(xNoun)
    const neighborhoodWord = skins[skin].vocab.neighborhood.singular
    const requestedFromNeighborhood =
      requestedSameColorCount > 1
        ? `from ${portfolioNeighborhoodOfRequestedCard.displayColor(
            skin
          )} ${neighborhoodWord} #${requestedSameColorIndex + 1}`
        : undefined
    const offeredFromNeighborhood =
      offeredSameColorCount > 1
        ? `from ${portfolioNeighborhoodOfOfferedCard.displayColor(
            skin
          )} ${neighborhoodWord} #${offeredSameColorIndex + 1}`
        : undefined
    const actorPossessive = xNounPossessive.startsWith(majorActor.username)
      ? 'their'
      : xNounPossessive

    return skins[skin].gameLogDescriptions.majorActions.forcedDeal({
      tense,
      actor,
      playVerb: conjugatedVerb,
      cardName: this.card.displayName(skin),
      targetPossessive: yNounPossessive,
      requestedCardName: this.requestedCard.displayName(skin),
      requestedFromNeighborhood,
      actorPossessive,
      offeredCardName: this.offeredCard.displayName(skin),
      offeredFromNeighborhood,
    })
  }
}
